import { useCartProduct, getInCartProductQty, isProductInCart } from 'cart'
import { I18n, Translate } from 'common'
import { ProductLocationModel, ProductModel } from 'interfaces'
import { ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import styled from 'styled-components'
import { ISeller } from '../../../../../../src/services/MultiSellerServicesProvider'
import { Vehicle } from '../../../../../../src/store/models/Vehicles'
import { defaultTheme } from '../../../../../../src/theme'
import { Button } from '../../../../../../src/ui-components'
import { isMiscellaneousVehicle } from '../../../../../../src/features/search/Results/utils/miscellaneousVehicle'
import QuantitySelectorWrapper from './QuantitySelectorWrapper'
import { formatVehicleDisplayWithOutEngine } from 'src/helpers/utils'

interface Props {
  seller: ISeller
  product: ProductModel
  location: ProductLocationModel
  vehicle: Vehicle
}

const VehicleCart = (props: Props): ReactElement => {
  const { vehicle, seller, product, location } = props
  const [selectorQty, setSelectorQty] = useState<number>(0)

  const isMiscellaneous = isMiscellaneousVehicle(vehicle)

  const cartProduct = useCartProduct({
    product,
    vehicle,
    sellerDetails: seller,
    locationId: location?.locationId,
  })

  const { handleAddVehiclePartToCart } = cartProduct

  const vehicleName = isMiscellaneous ? (
    <I18n name="miscellaneous" />
  ) : (
    formatVehicleDisplayWithOutEngine(vehicle, false)
  )
  const inCart = useSelector((state: RootState) =>
    isProductInCart(state, product, vehicle, seller?.coverageKey || '')
  )

  const inCartQty = useSelector((state: RootState) =>
    getInCartProductQty(
      state,
      vehicle,
      seller?.coverageKey ?? '',
      product,
      location.locationId
    )
  )

  useEffect(() => {
    if (inCart) {
      setSelectorQty(inCartQty)
    }
  }, [inCart, inCartQty])

  const inCartText = `${inCartQty} ${Translate('inCart')}`

  return (
    <CartItem key={`select_cart_drawer_cart_id_${vehicle?.id}`}>
      <VehicleName>{vehicleName}</VehicleName>
      <CTAs>
        <QuantitySelectorWrapper
          product={product}
          location={location}
          vehicle={vehicle}
          seller={seller}
          cartProduct={cartProduct}
          qty={selectorQty}
          setQty={setSelectorQty}
        />
        <ButtonWrapper>
          {inCart ? (
            <AddButton text={inCartText} variant="success" size="fullwidth" />
          ) : (
            <AddButton
              onClick={() => handleAddVehiclePartToCart(vehicle)}
              text="addToCart"
            />
          )}
        </ButtonWrapper>
      </CTAs>
    </CartItem>
  )
}

const CartItem = styled.div`
  display: flex;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  justify-content: space-between;
  padding-bottom: 32px;
  border-bottom: 1px solid ${defaultTheme.colors.border};
`

const VehicleName = styled.div`
  font-family: Oswald;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  letter-spacing: 0px;
`

const CTAs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 168px;
`

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`
const AddButton = styled(Button)`
  width: 100%;
  box-sizing: border-box;
  margin: 0;
`

export default VehicleCart
